'use client';

import useEmblaCarousel from 'embla-carousel-react';
import dynamic from 'next/dynamic';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { ContainerStyled } from '@/app/components/layouts/container';
import { PaginationDash } from '@/app/components/navigation/pagination';
import { useOverlayMountControl } from '@/app/components/overlays/hooks/useOverlayMountControl';
import { splashVisibility } from '@/app/components/splash/visibility';
import { OVERLAY_KEYS } from '@/app/constants/overlayKeys';
import { SectionHeroBannerItem, type SectionHeroBannerItemProps } from '@/app/cv/components/sections/home/hero-banner/item';
import { useMediaSlider } from '@/app/hooks/useMediaSlider';
import { cn } from '@/app/utils/cn';
import { createDataAttributes, type ExtendDataKeys } from '@/app/utils/createDataAttributes';
const IFrameVideoModal = dynamic(() => import('@/app/components/overlays/modal/video-modal').then(mod => mod.IFrameVideoModal), {
  ssr: false
});
export interface SectionHeroBannerProps {
  items: SectionHeroBannerItemProps[];
  title: string;
}
export const SectionHeroBanner = ({
  items = [],
  title
}: SectionHeroBannerProps) => {
  const isShowSplash = useSyncExternalStore(splashVisibility.subscribe, splashVisibility.get, () => false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    duration: 25
  });
  const [modalYoutube, setModalYoutube] = useState<{
    label: string;
    url: string;
  } | null>(null);
  const {
    paginationItemsRefs,
    currentIndex,
    currentPage,
    enableLoadVideo,
    setEmblaApi,
    onPageChange,
    onVideoEnded,
    onVideoReadyAndActive,
    onPlaySlide,
    onPauseSlide
  } = useMediaSlider({
    inView: !isShowSplash,
    items: items.map(item => item.video ?? item.videoDesktop)
  });
  const youtubeInstanceId = modalYoutube?.url || 'hero-banner-youtube-idle';
  const {
    shouldRender,
    open: openModal,
    close: closeModal
  } = useOverlayMountControl({
    overlayKey: OVERLAY_KEYS.MODAL_VIDEO,
    instanceId: youtubeInstanceId
  });
  useEffect(() => {
    if (!emblaApi) return;
    setEmblaApi(emblaApi);
  }, [emblaApi, setEmblaApi]);
  useEffect(() => {
    if (!modalYoutube?.url) return;
    openModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [modalYoutube?.url]);
  const handleYoutubeClick = (youtube: {
    label: string;
    url: string;
  }) => {
    onPauseSlide(currentIndex);
    setModalYoutube(youtube);
  };
  if (items.length === 0) return null;
  return <>
      <section className="h-screen-header relative" {...createDataAttributes({
      test: 'section-banner'
    })}>
        <h1 className="sr-only">{title}</h1>
        <div ref={emblaRef} className="size-full overflow-hidden">
          <div className="flex size-full will-change-transform">
            {items.map((item, index) => <div key={index} className="shrink-0 basis-full" {...createDataAttributes<ExtendDataKeys<'active'>>({
            test: `banner-item-${index}`,
            active: index === currentIndex ? 'true' : null
          })}>
                <SectionHeroBannerItem {...item} index={index} priority={index === 0} paused={currentIndex !== index || modalYoutube !== null} rewindWhenPaused={modalYoutube === null} preventLoadVideo={!enableLoadVideo || index !== currentIndex} onYoutubeClick={handleYoutubeClick} onReady={e => onVideoReadyAndActive(e, index)} onVideoEnded={() => {
              if (!modalYoutube) onVideoEnded(index);
            }} />
              </div>)}
          </div>
        </div>
        {items.length > 1 && <PaginationDash itemRefs={paginationItemsRefs} totalPages={items.length} currentPage={currentPage} wrapperClassName={cn('absolute right-0 bottom-10 left-0 z-10 justify-center lg:bottom-14 lg:justify-start', ContainerStyled())} isAnimated theme="light" onPageChange={onPageChange} />}

        {!!shouldRender && modalYoutube ? <IFrameVideoModal title={modalYoutube.label} instanceId={modalYoutube.url} youtubeId={modalYoutube.url} onClose={() => {
        closeModal();
        setModalYoutube(null);
        onPlaySlide(currentIndex);
      }} /> : null}
      </section>
    </>;
};